import * as THREE from "three";
import { loader } from "@/utils/loader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";

export interface HDRConfig {
  path: string;
  name: string;
  intensity?: number;
  rotation?: number;
  mapping?: "equirectangular" | "cube";
}

export class LightsManager {
  private static instance: LightsManager | null = null;

  private loadedLights: Map<string, THREE.Light> = new Map();
  currentHDR: THREE.Texture | null = null;
  private hdrLoader: RGBELoader;
  private pmremGenerator: THREE.PMREMGenerator | null = null;
  private isInitialized = false;

  private constructor() {
    this.hdrLoader = new RGBELoader();
    this.hdrLoader.setDataType(THREE.UnsignedByteType);
  }

  /**
   * Get the singleton instance of LightsManager
   */
  public static getInstance(): LightsManager {
    if (!LightsManager.instance) {
      LightsManager.instance = new LightsManager();
    }
    return LightsManager.instance;
  }

  /**
   * Initialize the LightsManager with a renderer
   */
  public initialize(renderer: THREE.WebGLRenderer): void {
    if (this.isInitialized) {
      console.warn("LightsManager is already initialized");
      return;
    }

    this.pmremGenerator = new THREE.PMREMGenerator(renderer);
    this.isInitialized = true;
  }

  /**
   * Check if the LightsManager is initialized
   */
  public getInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Load lights from the default JSON export packaged with the app.
   */
  async loadLightsFromJson(scene: THREE.Scene): Promise<THREE.Light[]> {
    try {
      const response = await fetch("/configs/lights/lights.json");
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const sceneData: any = await response.json();

      const lights: THREE.Light[] = [];

      // Determine where the light objects are in the JSON export
      const lightObjects: any[] = Array.isArray(sceneData)
        ? sceneData
        : Array.isArray(sceneData?.object)
        ? sceneData.object
        : Array.isArray(sceneData?.object?.children)
        ? sceneData.object.children
        : [];

      // Nothing to load
      if (!lightObjects.length) {
        return lights;
      }

      // Build each child that is a light
      await Promise.all(
        lightObjects.map(async (child: any) => {
          try {
            const type: string | undefined = child?.type;
            if (!type || !this.isLightType(type)) {
              return;
            }

            const light = this.createLightFromJson(child);
            if (light) {
              if (light.name) {
                this.loadedLights.set(light.name, light);
              }
              scene.add(light);
              lights.push(light);
            }
          } catch (e) {
            console.warn(
              "Skipping invalid light object",
              child?.name || child,
              e
            );
          }
        })
      );
      return lights;
    } catch (error) {
      console.error("Failed to parse scene export JSON:", error);
      throw error;
    }
  }

  private createLightFromJson(data: any): THREE.Light | null {
    const type = data?.type as string;
    const name = data?.name as string | undefined;
    const color = typeof data?.color === "number" ? data.color : 0xffffff;
    const intensity = typeof data?.intensity === "number" ? data.intensity : 1;

    let light: THREE.Light | null = null;

    switch (type) {
      case "AmbientLight":
        light = new THREE.AmbientLight(color, intensity);
        break;
      case "DirectionalLight": {
        const dir = new THREE.DirectionalLight(color, intensity);
        dir.castShadow = !!data?.castShadow;
        // Shadow camera options (best-effort)
        if (data?.shadow?.camera) {
          const cam = data.shadow.camera;
          dir.shadow.camera.near = cam.near ?? dir.shadow.camera.near;
          dir.shadow.camera.far = cam.far ?? dir.shadow.camera.far;
          // Orthographic extents when provided
          if (
            typeof cam.left === "number" &&
            typeof cam.right === "number" &&
            typeof cam.top === "number" &&
            typeof cam.bottom === "number"
          ) {
            const ortho = dir.shadow
              .camera as unknown as THREE.OrthographicCamera;
            ortho.left = cam.left;
            ortho.right = cam.right;
            ortho.top = cam.top;
            ortho.bottom = cam.bottom;
            ortho.updateProjectionMatrix();
          }
        }
        light = dir;
        break;
      }
      case "PointLight":
        light = new THREE.PointLight(color, intensity);
        break;
      case "SpotLight":
        light = new THREE.SpotLight(color, intensity);
        break;
      case "HemisphereLight":
        light = new THREE.HemisphereLight(color, 0x000000, intensity);
        break;
      case "RectAreaLight":
        // Note: requires RectAreaLightUniformsLib.init() for full support; create minimally
        // @ts-ignore - three types may not export RectAreaLight constructor typings the same way
        light = new (THREE as any).RectAreaLight(color, intensity);
        break;
      default:
        return null;
    }

    if (light) {
      if (name) light.name = name;

      // Apply basic properties
      if (typeof data?.castShadow === "boolean") {
        (light as any).castShadow = data.castShadow;
      }
      if (typeof data?.visible === "boolean") {
        light.visible = data.visible;
      }

      // Apply transform from matrix if available
      if (Array.isArray(data?.matrix) && data.matrix.length === 16) {
        const m = new THREE.Matrix4();
        m.fromArray(data.matrix);
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scl = new THREE.Vector3();
        m.decompose(pos, quat, scl);
        light.position.copy(pos);
        light.quaternion.copy(quat);
        light.scale.copy(scl);
      }
    }

    return light;
  }

  async addLight(type: string, scene: THREE.Scene): Promise<void> {
    let light: THREE.Light | undefined;
    switch (type) {
      case "AmbientLight":
        light = new THREE.AmbientLight();
        break;
      case "DirectionalLight": {
        light = new THREE.DirectionalLight();
        break;
      }
      case "PointLight":
        light = new THREE.PointLight();
        break;
      case "SpotLight":
        light = new THREE.SpotLight();
        break;
      case "HemisphereLight":
        light = new THREE.HemisphereLight();
        break;
      case "RectAreaLight":
        light = new (THREE as any).RectAreaLight();
        break;
    }
    if (light) {
      scene.add(light);
    }
  }

  /**
   * Check if an object type is a light
   */
  private isLightType(type: string): boolean {
    const lightTypes = [
      "AmbientLight",
      "DirectionalLight",
      "PointLight",
      "SpotLight",
      "HemisphereLight",
      "RectAreaLight"
    ];
    return lightTypes.includes(type);
  }

  /**
   * Get a specific light by name
   */
  getLight(name: string): THREE.Light | undefined {
    return this.loadedLights.get(name);
  }

  /**
   * Get all loaded lights
   */
  getAllLights(): THREE.Light[] {
    return Array.from(this.loadedLights.values());
  }

  /**
   * Clear all lights from the scene
   */
  clearLights(scene: THREE.Scene): void {
    this.loadedLights.forEach((light) => {
      scene.remove(light);
    });
    this.loadedLights.clear();
  }

  /**
   * Get available preset configurations
   */
  getAvailablePresets(): string[] {
    return [
      "/configs/lights/default-lights.json",
      "/configs/lights/dramatic-lights.json",
      "/configs/lights/studio-lights.json"
    ];
  }

  /**
   * Load HDR environment map for a specific mesh and its children
   */
  async loadHDR(
    object3D: THREE.Object3D,
    intensity: number = 1
  ): Promise<void> {
    try {
      if (!this.pmremGenerator) {
        console.error(
          "PMREMGenerator not initialized. Call initialize() first."
        );
        return;
      }

      this.pmremGenerator.compileEquirectangularShader();

      if (!this.currentHDR) {
        const texture = await this.hdrLoader
          .setDataType(THREE.UnsignedByteType)
          .loadAsync(this.getDefaultHDR());

        if (this.pmremGenerator) {
          const envMap =
            this.pmremGenerator.fromEquirectangular(texture).texture;
          texture.dispose();
          this.currentHDR = envMap;

          // Apply HDR to the object hierarchy
          this.applyHDRToObject(object3D, envMap, intensity);
        }
      } else if (this.currentHDR) {
        // Apply existing HDR to the object hierarchy
        this.applyHDRToObject(object3D, this.currentHDR, intensity);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Apply HDR environment map to a mesh and all its children
   */
  private applyHDRToObject(
    object3D: THREE.Object3D,
    envMap: THREE.Texture,
    intensity: number
  ): void {
    // Apply to the root if it's a mesh
    if ((object3D as any).isMesh) {
      const mesh = object3D as THREE.Mesh;
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.envMap = envMap;
              (mat as THREE.MeshStandardMaterial).envMapIntensity = intensity;
              mat.needsUpdate = true;
            }
          });
        } else if (mesh.material instanceof THREE.MeshStandardMaterial) {
          mesh.material.envMap = envMap;
          mesh.material.envMapIntensity = intensity;
          mesh.material.needsUpdate = true;
        }
      }
    }

    // Apply to all children recursively
    object3D.traverse((child) => {
      if ((child as any).isMesh) {
        const m = child as THREE.Mesh;
        if (m.material) {
          if (Array.isArray(m.material)) {
            m.material.forEach((mat) => {
              if (mat instanceof THREE.MeshStandardMaterial) {
                mat.envMap = envMap;
                (mat as THREE.MeshStandardMaterial).envMapIntensity = intensity;
                mat.needsUpdate = true;
              }
            });
          } else if (m.material instanceof THREE.MeshStandardMaterial) {
            m.material.envMap = envMap;
            m.material.envMapIntensity = intensity;
            m.material.needsUpdate = true;
          }
        }
      }
    });
  }

  getDefaultHDR(): string {
    return "/resources/textures/barnaslingan_02_1k.hdr";
  }

  /**
   * Load HDR environment map for a specific material
   */
  async loadHDRForMaterial(
    material: THREE.MeshStandardMaterial,
    intensity: number = 1
  ): Promise<void> {
    try {
      if (!this.pmremGenerator) {
        console.error(
          "PMREMGenerator not initialized. Call initialize() first."
        );
        return;
      }

      this.pmremGenerator.compileEquirectangularShader();

      if (!this.currentHDR) {
        const texture = await this.hdrLoader
          .setDataType(THREE.UnsignedByteType)
          .loadAsync(this.getDefaultHDR());

        if (this.pmremGenerator) {
          const envMap =
            this.pmremGenerator.fromEquirectangular(texture).texture;
          texture.dispose();
          this.currentHDR = envMap;
        }
      }

      if (this.currentHDR) {
        material.envMap = this.currentHDR;
        material.envMapIntensity = intensity;
        material.needsUpdate = true;
      }
    } catch (error) {
      console.error("Failed to load HDR for material:", error);
      throw error;
    }
  }

  /**
   * Get all lights from a scene
   */
  getLightsFromScene(scene: THREE.Scene): THREE.Light[] {
    const lights: THREE.Light[] = [];
    scene.traverse((child) => {
      if ((child as any).isLight) {
        lights.push(child as THREE.Light);
      }
    });
    return lights;
  }

  /**
   * Export lights from scene to JSON format
   */
  exportLightsToJSON(scene: THREE.Scene): string {
    const lights = this.getLightsFromScene(scene);
    const lightsData = lights.map((light) => ({
      type: light.type,
      name: light.name,
      color: light.color.getHex(),
      intensity: light.intensity,
      position: light.position.toArray(),
      rotation: light.rotation.toArray(),
      scale: light.scale.toArray(),
      matrix: light.matrix.toArray(),
      castShadow: (light as any).castShadow || false,
      visible: light.visible,
      // Include light-specific properties
      ...(light instanceof THREE.DirectionalLight && {
        shadow: light.shadow
          ? {
              camera: {
                near: light.shadow.camera.near,
                far: light.shadow.camera.far,
                left: (light.shadow.camera as any).left,
                right: (light.shadow.camera as any).right,
                top: (light.shadow.camera as any).top,
                bottom: (light.shadow.camera as any).bottom
              }
            }
          : undefined
      })
    }));

    return JSON.stringify(lightsData, null, 2);
  }

  /**
   * Import lights from JSON data
   */
  async importLightsFromJSON(
    scene: THREE.Scene,
    jsonData: string
  ): Promise<void> {
    try {
      const lightsData = JSON.parse(jsonData);

      // Clear existing lights first
      this.clearLights(scene);

      // Create lights from data
      for (const lightData of lightsData) {
        const light = this.createLightFromJson(lightData);
        if (light) {
          if (light.name) {
            this.loadedLights.set(light.name, light);
          }
          scene.add(light);
        }
      }
    } catch (error) {
      console.error("Failed to import lights from JSON:", error);
      throw error;
    }
  }

  /**
   * Remove a specific light from the scene
   */
  removeLight(scene: THREE.Scene, light: THREE.Light): void {
    scene.remove(light);
    if (light.name) {
      this.loadedLights.delete(light.name);
    }
    // Dispose light if it has a dispose method (some light types do)
    if (
      (light as any).dispose &&
      typeof (light as any).dispose === "function"
    ) {
      (light as any).dispose();
    }
  }

  /**
   * Update light properties
   */
  updateLightProperty(light: THREE.Light, property: string, value: any): void {
    switch (property) {
      case "color":
        light.color.setHex(value.replace("#", "0x"));
        break;
      case "intensity":
        light.intensity = parseFloat(value);
        break;
      case "position":
        if (value.x !== undefined) light.position.x = value.x;
        if (value.y !== undefined) light.position.y = value.y;
        if (value.z !== undefined) light.position.z = value.z;
        break;
      case "rotation":
        if (value.x !== undefined) light.rotation.x = value.x;
        if (value.y !== undefined) light.rotation.y = value.y;
        if (value.z !== undefined) light.rotation.z = value.z;
        break;
      case "scale":
        if (value.x !== undefined) light.scale.x = value.x;
        if (value.y !== undefined) light.scale.y = value.y;
        if (value.z !== undefined) light.scale.z = value.z;
        break;
    }
  }

  /**
   * Get light types available for creation
   */
  getAvailableLightTypes(): string[] {
    return [
      "AmbientLight",
      "DirectionalLight",
      "PointLight",
      "SpotLight",
      "HemisphereLight",
      "RectAreaLight"
    ];
  }
}
